import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import { Navbar } from "../components/Navbar";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { 
  Search, Building2, Wrench, Loader2, ArrowRight, 
  CheckCircle, Sparkles, Crown
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLES = [
  {
    id: "candidate",
    title: "Cerca Lavoro",
    subtitle: "Candidato",
    description: "Sfoglia tutti gli annunci di lavoro nel Canton Ticino e candidati alle posizioni aperte.",
    icon: Search,
    color: "#DA291C",
    bgColor: "#DA291C10",
    features: [
      "Visualizza tutti gli annunci",
      "Candidatura illimitata",
      "Profilo visibile alle aziende"
    ],
    badge: "GRATIS",
    badgeColor: "#10B981",
    note: null
  },
  {
    id: "company",
    title: "Pubblica Annuncio di Lavoro",
    subtitle: "Azienda",
    description: "Pubblica offerte di lavoro gratuitamente e trova i migliori talenti della regione.",
    icon: Building2,
    color: "#004E64",
    bgColor: "#004E6410",
    features: [
      "Pubblica annunci gratis",
      "Gestisci candidature",
      "Contatta i candidati"
    ],
    badge: "GRATIS",
    badgeColor: "#10B981",
    note: null
  },
  {
    id: "service_provider",
    title: "Offri i tuoi Servizi",
    subtitle: "Professionista",
    description: "Promuovi i tuoi servizi professionali e raggiungi nuovi clienti nel Canton Ticino.",
    icon: Wrench,
    color: "#10B981",
    bgColor: "#10B98110",
    features: [
      "Vetrina servizi dedicata",
      "Contatti diretti clienti",
      "Visibilità premium"
    ],
    badge: "PREMIUM",
    badgeColor: "#F59E0B",
    note: "Abbonamento CHF 24/anno"
  }
];

export default function RoleSelectionPage() {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const [loading, setLoading] = useState(null);

  const handleSelectRole = async (roleId) => {
    setLoading(roleId);
    
    try {
      const token = localStorage.getItem("token");
      
      // Aggiorna il tipo utente nel backend
      const response = await axios.post(`${API}/auth/set-role`, 
        { user_type: roleId },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.user) {
        // Aggiorna localStorage
        localStorage.setItem("user", JSON.stringify(response.data.user));
        
        // Refresh del contesto auth
        await refreshUser();
        
        toast.success(`Profilo ${ROLES.find(r => r.id === roleId)?.subtitle} attivato!`);
        
        // Reindirizza alla dashboard appropriata
        switch (roleId) {
          case "candidate":
            navigate("/jobs"); // Va a vedere gli annunci
            break;
          case "company":
            navigate("/dashboard/company");
            break;
          case "service_provider":
            navigate("/subscription"); // Va al paywall
            break;
          default:
            navigate("/");
        }
      }
    } catch (error) {
      const message = error.response?.data?.detail || "Errore durante la selezione";
      toast.error(message);
    } finally {
      setLoading(null);
    }
  };

  // Se l'utente ha già un ruolo, reindirizza
  if (user && user.user_type && user.user_type !== "pending") {
    const dashboardMap = {
      candidate: "/dashboard/candidate",
      company: "/dashboard/company",
      service_provider: "/dashboard/provider"
    };
    navigate(dashboardMap[user.user_type] || "/");
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white" data-testid="role-selection-page">
      <Navbar />
      
      <div className="max-w-4xl mx-auto px-4 py-8 sm:py-12">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-50 text-emerald-700 rounded-full text-sm font-medium mb-4">
            <CheckCircle className="w-4 h-4" />
            Account creato con successo!
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-3">
            Cosa vuoi fare su LavoroTicino?
          </h1>
          <p className="text-slate-600 max-w-lg mx-auto">
            Scegli il tuo percorso. Potrai sempre cambiare idea più tardi.
          </p>
        </div>

        {/* Role Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
          {ROLES.map((role) => {
            const Icon = role.icon;
            const isLoading = loading === role.id;
            
            return (
              <div
                key={role.id}
                className="bg-white rounded-2xl border-2 border-slate-200 hover:border-slate-300 shadow-sm hover:shadow-lg transition-all duration-200 overflow-hidden flex flex-col"
                data-testid={`role-card-${role.id}`}
              >
                {/* Badge */}
                {role.badge && (
                  <div 
                    className="text-white text-xs font-bold py-1.5 text-center"
                    style={{ backgroundColor: role.badgeColor }}
                  >
                    {role.badge === "PREMIUM" && <Crown className="w-3 h-3 inline mr-1" />}
                    {role.badge}
                  </div>
                )}
                
                <div className="p-6 flex flex-col flex-grow">
                  {/* Icon */}
                  <div 
                    className="w-14 h-14 rounded-xl flex items-center justify-center mb-4"
                    style={{ backgroundColor: role.bgColor }}
                  >
                    <Icon className="w-7 h-7" style={{ color: role.color }} />
                  </div>

                  {/* Title */}
                  <h2 className="text-lg font-bold text-slate-900 mb-1">
                    {role.title}
                  </h2>
                  <p className="text-sm text-slate-500 mb-3">{role.subtitle}</p>
                  
                  {/* Description */}
                  <p className="text-sm text-slate-600 mb-4 flex-grow">
                    {role.description}
                  </p>

                  {/* Features */}
                  <ul className="space-y-2 mb-5">
                    {role.features.map((feature, i) => (
                      <li key={i} className="flex items-center gap-2 text-sm text-slate-700">
                        <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>

                  {/* Note */}
                  {role.note && (
                    <p className="text-xs text-slate-500 mb-4 flex items-center gap-1">
                      <Sparkles className="w-3 h-3" />
                      {role.note}
                    </p>
                  )}

                  {/* Button */}
                  <Button
                    onClick={() => handleSelectRole(role.id)}
                    disabled={loading !== null}
                    className="w-full h-11 font-semibold text-white"
                    style={{ backgroundColor: role.color }}
                    data-testid={`select-role-${role.id}`}
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Attivazione...
                      </>
                    ) : (
                      <>
                        Scegli
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </>
                    )}
                  </Button>
                </div>
              </div>
            );
          })}
        </div>

        {/* Info Banner */}
        <div className="mt-10 bg-slate-50 rounded-xl p-6 text-center">
          <p className="text-sm text-slate-600">
            <strong>Non sei sicuro?</strong> Puoi sempre cambiare il tuo ruolo dalle impostazioni del profilo.
            <br />
            Le aziende possono anche attivare la sezione servizi in qualsiasi momento.
          </p>
        </div>
      </div>
    </div>
  );
}
